import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Card, CardContent, Stack, TextField, MenuItem, Button, Table, TableBody, TableCell, TableHead, TableRow,
  TableContainer, Chip, Typography, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions,
  Alert, FormControlLabel, Checkbox, RadioGroup, Radio,
} from '@mui/material';
import { Search, Download, Sync, Undo, ReceiptLong, CheckCircle, History } from '@mui/icons-material';
import { api } from '../../config/api';

const clp = (n: any) => `$${Math.round(Number(n || 0)).toLocaleString('es-CL')}`;
const fh = (d?: string) => (d ? new Date(d).toLocaleString('es-CL', { dateStyle: 'short', timeStyle: 'short' }) : '—');
const COLOR: Record<string, any> = { pagado: 'success', pendiente: 'warning', rechazado: 'error', revision: 'warning', expirado: 'default', reemplazado: 'default' };

const AdminPagos: React.FC<{ onCambio?: () => void }> = ({ onCambio }) => {
  const [f, setF] = useState({ buscar: '', estado: '', tipo: '', desde: '', hasta: '' });
  const [pagos, setPagos] = useState<any[]>([]);
  const [totales, setTotales] = useState<any>(null);
  const [msg, setMsg] = useState<{ sev: any; txt: string } | null>(null);
  const [reemb, setReemb] = useState<any>(null);
  const [rf, setRf] = useState({ monto: '', motivo: '', modo: 'flow', revocar: false });
  const [boleta, setBoleta] = useState<any>(null);
  const [bf, setBf] = useState({ folio: '', url: '' });
  const [eventos, setEventos] = useState<any[] | null>(null);

  const params = () => Object.fromEntries(Object.entries(f).filter(([, v]) => v));

  const cargar = useCallback(async () => {
    const { data } = await api.get('/admin/pagos', { params: Object.fromEntries(Object.entries(f).filter(([, v]) => v)) });
    setPagos(data.pagos || []); setTotales(data.totales);
  }, [f]);
  useEffect(() => { cargar(); }, []); // solo al montar; luego se filtra con el botón

  const exportar = async () => {
    const r = await api.get('/admin/pagos/export', { params: params(), responseType: 'blob' });
    const url = URL.createObjectURL(r.data);
    const a = document.createElement('a'); a.href = url; a.download = `pagos-trespondo-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  };

  const run = async (fn: () => Promise<any>, ok: (d: any) => string) => {
    setMsg(null);
    try { const r = await fn(); setMsg({ sev: 'success', txt: ok(r.data) }); await cargar(); onCambio?.(); }
    catch (e: any) { setMsg({ sev: 'error', txt: e.response?.data?.error || 'Error' }); }
  };

  const verEventos = async (p: any) => {
    const { data } = await api.get('/admin/flow-eventos', { params: { orden: p.commerce_order } });
    setEventos(data.eventos || []);
  };

  return (
    <Box>
      {msg && <Alert severity={msg.sev} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.txt}</Alert>}
      <Card sx={{ mb: 2 }}><CardContent>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
          <TextField size="small" label="Buscar orden, email o nombre" value={f.buscar} onChange={(e) => setF({ ...f, buscar: e.target.value })}
            onKeyDown={(e) => { if (e.key === 'Enter') cargar(); }} sx={{ flex: 2 }} />
          <TextField size="small" select label="Estado" value={f.estado} onChange={(e) => setF({ ...f, estado: e.target.value })} sx={{ minWidth: 140 }}>
            <MenuItem value="">Todos</MenuItem>
            {['pagado', 'pendiente', 'revision', 'rechazado', 'expirado', 'reemplazado'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField size="small" select label="Tipo" value={f.tipo} onChange={(e) => setF({ ...f, tipo: e.target.value })} sx={{ minWidth: 160 }}>
            <MenuItem value="">Todos</MenuItem>
            {['suscripcion', 'cambio_plan', 'renovacion_auto', 'recarga_saldo'].map((s) => <MenuItem key={s} value={s}>{s}</MenuItem>)}
          </TextField>
          <TextField size="small" type="date" label="Desde" InputLabelProps={{ shrink: true }} value={f.desde} onChange={(e) => setF({ ...f, desde: e.target.value })} />
          <TextField size="small" type="date" label="Hasta" InputLabelProps={{ shrink: true }} value={f.hasta} onChange={(e) => setF({ ...f, hasta: e.target.value })} />
          <Button variant="contained" startIcon={<Search />} onClick={cargar}>Filtrar</Button>
          <Button variant="outlined" startIcon={<Download />} onClick={exportar}>CSV</Button>
        </Stack>
        {totales && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {totales.cantidad} pagos · pagado {clp(totales.pagado)} · reembolsado {clp(totales.reembolsado)}
          </Typography>
        )}
      </CardContent></Card>

      <Card><TableContainer sx={{ maxHeight: '65vh' }}>
        <Table size="small" stickyHeader>
          <TableHead><TableRow>
            <TableCell>Fecha</TableCell><TableCell>Usuario</TableCell><TableCell>Orden (Flow)</TableCell><TableCell>Tipo</TableCell>
            <TableCell align="right">Monto</TableCell><TableCell>Estado</TableCell><TableCell>Comprobante</TableCell><TableCell>Acciones</TableCell>
          </TableRow></TableHead>
          <TableBody>
            {pagos.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell><Typography variant="caption">{fh(p.pagado_at || p.created_at)}</Typography></TableCell>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.usuario_nombre || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">{p.usuario_email}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>{p.commerce_order}</Typography>
                  <Typography variant="caption" display="block" color="text.secondary">{p.proyecto}{p.flow_order ? ` · Flow #${p.flow_order}` : ''}{p.medio_pago ? ` · ${p.medio_pago}` : ''}</Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">{p.tipo}</Typography>
                  {p.plan_codigo && <Typography variant="caption" color="text.secondary">{p.plan_codigo}</Typography>}
                  {p.cupon_codigo && <Typography variant="caption" display="block" color="success.main">{p.cupon_codigo} -{clp(p.descuento)}</Typography>}
                </TableCell>
                <TableCell align="right">
                  {clp(p.monto)}
                  {p.monto_flow != null && Number(p.monto_flow) !== Number(p.monto) && <Typography variant="caption" display="block" color="error">Flow: {clp(p.monto_flow)}</Typography>}
                </TableCell>
                <TableCell>
                  <Chip size="small" color={COLOR[p.estado] || 'default'} label={p.estado} />
                  {p.reembolso_estado && <Chip size="small" sx={{ ml: 0.5 }} color="secondary" label={`reembolso ${p.reembolso_estado} ${clp(p.reembolso_monto)}`} />}
                  {p.error && <Tooltip title={p.error}><Typography variant="caption" display="block" color="error" noWrap sx={{ maxWidth: 160 }}>{p.error}</Typography></Tooltip>}
                </TableCell>
                <TableCell>
                  <Typography variant="caption">{p.comprobante_numero ? `N° ${p.comprobante_numero}` : '—'}</Typography>
                  {p.boleta_folio && <Typography variant="caption" display="block">Boleta {p.boleta_folio}</Typography>}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.3}>
                    {['pendiente', 'expirado', 'rechazado'].includes(p.estado) && (
                      <Tooltip title="Consultar estado en Flow"><IconButton size="small" onClick={() => run(() => api.post(`/admin/pagos/${p.id}/verificar`), (d) => `Resultado: ${d.resultado}`)}><Sync fontSize="small" /></IconButton></Tooltip>
                    )}
                    {p.estado === 'revision' && (
                      <Tooltip title="Aprobar (monto revisado en Flow)"><IconButton size="small" color="success" onClick={() => { if (window.confirm('¿Aprobar este pago con el monto informado por Flow?')) run(() => api.post(`/admin/pagos/${p.id}/aprobar`), () => 'Pago aprobado y aplicado'); }}><CheckCircle fontSize="small" /></IconButton></Tooltip>
                    )}
                    {p.estado === 'pagado' && (!p.reembolso_estado || p.reembolso_estado === 'rechazado') && (
                      <Tooltip title="Reembolsar"><IconButton size="small" onClick={() => { setReemb(p); setRf({ monto: String(p.monto), motivo: '', modo: 'flow', revocar: false }); }}><Undo fontSize="small" /></IconButton></Tooltip>
                    )}
                    {p.estado === 'pagado' && (
                      <Tooltip title="Registrar boleta/factura SII"><IconButton size="small" onClick={() => { setBoleta(p); setBf({ folio: p.boleta_folio || '', url: p.boleta_url || '' }); }}><ReceiptLong fontSize="small" /></IconButton></Tooltip>
                    )}
                    <Tooltip title="Notificaciones de Flow"><IconButton size="small" onClick={() => verEventos(p)}><History fontSize="small" /></IconButton></Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
            {pagos.length === 0 && <TableRow><TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>Sin pagos</TableCell></TableRow>}
          </TableBody>
        </Table>
      </TableContainer></Card>

      {/* Reembolso */}
      <Dialog open={!!reemb} onClose={() => setReemb(null)} fullWidth maxWidth="xs">
        <DialogTitle>Reembolsar {reemb?.commerce_order}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Monto" type="number" value={rf.monto} onChange={(e) => setRf({ ...rf, monto: e.target.value })} helperText={`Máximo ${clp(reemb?.monto)}`} />
            <TextField label="Motivo" value={rf.motivo} onChange={(e) => setRf({ ...rf, motivo: e.target.value })} />
            <RadioGroup value={rf.modo} onChange={(e) => setRf({ ...rf, modo: e.target.value })}>
              <FormControlLabel value="flow" control={<Radio />} label="Solicitar a Flow (devuelve el dinero al medio de pago)" />
              <FormControlLabel value="manual" control={<Radio />} label="Ya lo reembolsé por fuera (solo registrar)" />
            </RadioGroup>
            <FormControlLabel control={<Checkbox checked={rf.revocar} onChange={(e) => setRf({ ...rf, revocar: e.target.checked })} />}
              label={reemb?.tipo === 'recarga_saldo' ? 'Descontar el monto del saldo del usuario' : 'Dejar la suscripción vencida (el bot deja de responder)'} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReemb(null)}>Cancelar</Button>
          <Button color="error" variant="contained" onClick={() => {
            const p = reemb; setReemb(null);
            run(() => api.post(`/admin/pagos/${p.id}/reembolsar`, { ...rf, monto: parseInt(rf.monto, 10) }), () => 'Reembolso registrado');
          }}>Reembolsar</Button>
        </DialogActions>
      </Dialog>

      {/* Boleta */}
      <Dialog open={!!boleta} onClose={() => setBoleta(null)} fullWidth maxWidth="xs">
        <DialogTitle>Boleta / factura SII</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Registra el folio del documento emitido en el SII (o tu facturador) para {boleta?.commerce_order}. El usuario lo verá en su historial.
          </Typography>
          <Stack spacing={2}>
            <TextField label="Folio" value={bf.folio} onChange={(e) => setBf({ ...bf, folio: e.target.value })} />
            <TextField label="URL del PDF (https://...)" value={bf.url} onChange={(e) => setBf({ ...bf, url: e.target.value })} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBoleta(null)}>Cancelar</Button>
          <Button variant="contained" onClick={() => { const p = boleta; setBoleta(null); run(() => api.put(`/admin/pagos/${p.id}/boleta`, bf), () => 'Boleta registrada'); }}>Guardar</Button>
        </DialogActions>
      </Dialog>

      {/* Eventos Flow */}
      <Dialog open={!!eventos} onClose={() => setEventos(null)} fullWidth maxWidth="md">
        <DialogTitle>Notificaciones de Flow</DialogTitle>
        <DialogContent dividers>
          {eventos?.length === 0 && <Typography color="text.secondary">Sin notificaciones registradas para esta orden.</Typography>}
          {eventos?.map((e) => (
            <Box key={e.id} sx={{ mb: 1.5 }}>
              <Typography variant="body2"><b>{fh(e.created_at)}</b> · {e.tipo} · {e.resultado}</Typography>
              <Typography variant="caption" component="pre" sx={{ whiteSpace: 'pre-wrap', bgcolor: '#f8fafc', p: 1, borderRadius: 1, display: 'block' }}>
                {JSON.stringify(e.detalle, null, 2)}
              </Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions><Button onClick={() => setEventos(null)}>Cerrar</Button></DialogActions>
      </Dialog>
    </Box>
  );
};

export default AdminPagos;
